import { useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, Loader2 } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { NewTaskDotBackground } from "./canvas/NewTaskDotBackground";
import "./workspace-auth.css";

export default function Login({ onLogin, variant = "standard" }: { onLogin: () => void | Promise<void>; variant?: "standard" | "desktop" }) {
  const isDesktop = variant === "desktop";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();
  const submittingRef = useRef(false);
  async function finishLogin() {
    if (isDesktop) {
      await onLogin();
      return;
    }
    const target = new URLSearchParams(window.location.search).get('returnTo') || '';
    // Only resume our own OAuth or explicit generation approval page.
    if (target.startsWith('/oauth/authorize?') || /^\/connections\/approve\/[a-f0-9-]{36}$/.test(target)) {
      window.location.assign(target);
      return;
    }
    onLogin(); setLocation('/brands');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || "Login failed");
        return;
      }

      await finishLogin();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <main className="workspace-auth" data-testid="login-page">
      <NewTaskDotBackground />
      <div className="workspace-auth__shell">
        <div className="workspace-auth__logo" data-testid="text-logo">
          <img src="/admoji-logo.png" alt="Admoji" width={128} />
        </div>

        <section className="workspace-auth__card" aria-labelledby="login-title" data-ripple-block>
          <header className="workspace-auth__heading">
            <p className="workspace-auth__eyebrow">{isDesktop ? "ADMOJI DESKTOP" : "YOUR CREATIVE WORKSPACE"}</p>
            <h1 id="login-title">{isDesktop ? "Sign in to connect." : "Welcome back."}</h1>
            <p className="workspace-auth__intro">{isDesktop ? "Sign in with the account you use on the web. You'll review the connection next." : "Sign in to pick up where you left off."}</p>
          </header>

          <form onSubmit={handleSubmit} className="workspace-auth__form" aria-busy={loading}>
            {error && (
              <div className="workspace-auth__error" id="login-error" role="alert" data-testid="text-login-error">
                {error}
              </div>
            )}
            <div className="workspace-auth__field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                aria-required="true"
                aria-describedby={error ? "login-error" : undefined}
                data-testid="input-email"
              />
            </div>

            <div className="workspace-auth__field">
              <div className="workspace-auth__label-row">
                <label htmlFor="password">Password</label>
                <Link href="/forgot-password" target={isDesktop ? "_blank" : undefined} rel={isDesktop ? "noopener noreferrer" : undefined} data-testid="link-forgot-password">Forgot password?</Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                aria-required="true"
                aria-describedby={error ? "login-error" : undefined}
                data-testid="input-password"
              />
            </div>

            <button type="submit" disabled={loading} data-testid="button-login" className="workspace-auth__submit">
              {loading ? (
                <><Loader2 className="workspace-auth__spinner" aria-hidden="true" />Signing in...</>
              ) : (
                <>Sign in<ArrowRight aria-hidden="true" /></>
              )}
            </button>

            {!isDesktop && import.meta.env.VITE_GOOGLE_CLIENT_ID && (
              <div className="workspace-auth__google">
                <div className="workspace-auth__divider"><span>or continue with</span></div>
                <GoogleSignInButton
                  theme="outline"
                  onSuccess={finishLogin}
                  onError={setError}
                />
              </div>
            )}
          </form>

          <p className="workspace-auth__signup">
            New to Admoji? <Link href="/signup" target={isDesktop ? "_blank" : undefined} rel={isDesktop ? "noopener noreferrer" : undefined}>Create an account<ArrowRight aria-hidden="true" /></Link>
          </p>
        </section>
        <p className="workspace-auth__footer">Your next great ad starts here.</p>
      </div>
    </main>
  );
}
